import type {
  FinancialRecord,
  FinancialRecordsBuild,
  RecordsByGroup,
  SubtopicRecord,
} from '#calculator/index'
import { getCategoryExperience } from '#dictionary'

import type { SubmissionDoc } from '.'

export interface SubmissionItem<T = SubmissionDoc> {
  submission: T
  record: FinancialRecord | undefined
  subtopicRecords?: SubtopicRecord[]
  recordsByGroup?: RecordsByGroup
  visibleSubtopicIds?: string[]
}

export interface Section {
  id: string
  title: string
}

// Generic over the item shape so grouping works for both `SubmissionItem`
// (Plan tab) and `QuestSubmissionItem` (quests) without adaptation.
export interface SubmissionGroup<TItem = SubmissionItem> {
  section: Section
  items: TItem[]
}

export type BuildSubmissionTree<T = SubmissionDoc> = (
  submission: T
) => FinancialRecordsBuild

// Runs `build` for every submission and shapes the result into flat
// `SubmissionItem`s. Callers that only need a list (e.g. by-id lookups)
// stop here; callers that render sections layer `groupSubmissionItems`
// on top.
export const buildSubmissionItems = <T>(
  submissions: T[],
  build: (submission: T) => FinancialRecordsBuild
): SubmissionItem<T>[] =>
  submissions.map(submission => {
    const result = build(submission)
    const tree = result.tree
    return {
      submission,
      record: tree?.financialRecord,
      subtopicRecords: tree?.subtopicRecords,
      recordsByGroup: tree?.recordsByGroup,
      visibleSubtopicIds: result.visibleSubtopicIds,
    }
  })

// Anything with an optional `record` can be bucketed by experience —
// `groupSubmissionItems` doesn't care about the rest of the shape.
// `QuestSubmissionItem` (renames `submission` → `questDoc` and adds
// `template`) fits without adaptation.
interface GroupableItem {
  record: FinancialRecord | undefined
}

// Partitions pre-built items into experience sections, sorted by annual
// value within each group. Items without a record fall into a trailing
// "Other" bucket.
export const groupSubmissionItems = <TItem extends GroupableItem>(
  items: TItem[]
): SubmissionGroup<TItem>[] => {
  const groupMap = new Map<string, SubmissionGroup<TItem>>()
  const ungrouped: TItem[] = []

  for (const item of items) {
    if (!item.record) {
      ungrouped.push(item)
      continue
    }
    const experience = getCategoryExperience(item.record.finCategory)
    const existing = groupMap.get(experience.key)
    if (existing) {
      existing.items.push(item)
    } else {
      groupMap.set(experience.key, {
        section: { id: experience.key, title: experience.name },
        items: [item],
      })
    }
  }

  const groups = Array.from(groupMap.values())
  for (const group of groups) {
    group.items.sort(
      (a, b) => (b.record?.annualValue ?? 0) - (a.record?.annualValue ?? 0)
    )
  }
  if (ungrouped.length > 0) {
    groups.push({
      section: { id: 'ungrouped', title: 'Other' },
      items: ungrouped,
    })
  }
  return groups
}
